import { useEffect } from 'react'
import { useWebflowTabs } from '@/components/chrome/useWebflowTabs'
import { useInViewReveal } from '@/components/chrome/useInViewReveal'
import { useRoiCalculator } from '@/components/chrome/useRoiCalculator'

/**
 * The two runtime behaviours the ported markup still needs, and the tabs.
 *
 * Until Phase 6 this also loaded jQuery and Webflow's interactions engine —
 * 331 KB — to reveal `opacity: 0` initial states, run the tab panels and open
 * the nav. None of that is here now: the initial states are gone from the
 * markup, the tabs are `useWebflowTabs`, and the nav is `useChromeNav`.
 *
 * What remains is the wordmark fetch and the promo strip's close button, both
 * plain DOM. The header's contrast pass used to be here too; it now sits in
 * `SiteChrome`, which renders the header on every route — this only mounts on
 * two of them, which is why half the site went unmeasured.
 */
export function WebflowBinder({ route }: { route: string }) {
  useWebflowTabs(route)
  /*
   * `#hundred` on /marketing-automation, whose reveal class its embed's
   * dropped <script> used to add. Without this the graphic is opacity: 0
   * forever — see useInViewReveal.
   */
  useInViewReveal(route)
  /*
   * /roi-calculator's arithmetic, recovered from the Webflow staging origin.
   * Its embed's <script> was dropped by the porter, leaving 44 ids wired to
   * nothing — see useRoiCalculator.
   */
  useRoiCalculator(route)

  /*
   * The wordmark.
   *
   * The capture fetches it from a GitHub raw URL and appends it into
   * `#ajaxContent` (the `.logowrapper` inside the brand link), so the markup
   * ships with an empty container. Vendored to /wf/static-logo.svg so the
   * header does not depend on a third party being up.
   */
  useEffect(() => {
    const target = document.getElementById('ajaxContent')
    if (!target || target.querySelector('svg')) return
    let cancelled = false
    fetch('/wf/static-logo.svg')
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((markup) => {
        if (cancelled || target.querySelector('svg')) return
        target.insertAdjacentHTML('beforeend', markup)
        // The header's contrast pass runs before this lands, so re-apply the
        // fill it would have set.
        const header = document.getElementById('header')
        if (header) {
          const dark = header.classList.contains('dark')
          target.querySelectorAll<SVGPathElement>('svg path').forEach((p) => {
            p.style.fill = dark ? '#fff' : '#000'
          })
        }
      })
      .catch(() => {
        /* the brand link still routes home without its mark */
      })
    return () => {
      cancelled = true
    }
  }, [route])

  // The promo strip's close button was an inline `onclick` in the capture.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const close = (e.target as HTMLElement).closest('.promo-close')
      if (!close) return
      e.preventDefault()
      document.getElementById('promo-header')?.remove()
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [])

  return null
}

